import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/** Contractele lui /api/v2/alarms/fleet/{door} si .../table (App\Alarms\AlarmFleet). */

export const DOORS = ['opteemum', 'proactivitee', 'securitee'] as const
export type Door = (typeof DOORS)[number]

export function isDoor(value: string): value is Door {
  return (DOORS as readonly string[]).includes(value)
}

export interface NoiseRow {
  type: number
  name: string
  opened: number
  machines: number
  per_machine: number
  flaps: number
  flap_pct: number
  avg_minutes: number | null
  still_open: number
}

export interface Noise {
  days: number
  total: number
  types: number
  flaps: number
  flap_minutes: number
  rows: NoiseRow[]
}

export interface FleetPage {
  door: Door
  doors: Door[]
  tone: string
  icon: string
  title: string
  note: string
  intro: string
  settings: { url: string; label: string } | null
  /** Filele randului desfasurat, dupa drepturi (alarms/details/tabList.php). */
  tabs: { summary: boolean; active: boolean }
  counters: { info: number; minor: number; major: number; critical: number }
  open: number
  noise: Noise | null
}

export function fleetQuery(door: Door) {
  return queryOptions({
    queryKey: ['alarms', 'fleet', door],
    queryFn: () => apiGet<FleetPage>(`/alarms/fleet/${door}`),
  })
}

/** Un rand din it_opteemum_alerts_settings, cum il scrie Alarms::jsonData. */
export interface AlarmRow {
  ID: number
  type_name: string
  sign: string | null
  convert: string | null
  category_name: string
  flag: number | string
  from_value: number | string | null
  to_value: number | string | null
  dinamic: number | string
  status: number | string
  affected_devices: number
  DT_RowId: string
}

export interface AlarmTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: AlarmRow[]
}

export interface AlarmTableRequest {
  door: Door
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function alarmTableParams(r: AlarmTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function alarmTableQuery(r: AlarmTableRequest) {
  return queryOptions({
    queryKey: ['alarms', 'table', r],
    queryFn: () => apiGet<AlarmTablePage>(`/alarms/fleet/${r.door}/table?${alarmTableParams(r).toString()}`),
  })
}

/** Fila Summary: alarmSummary + alarms7Days din controllerele vechi, intr-un raspuns. */
export interface AlarmSummary {
  counters: { active: number; closed: number; total: number; closed_by_users: number }
  series: { categories: string[]; data: number[] }
}

export function alarmSummaryQuery(door: Door, id: number) {
  return queryOptions({
    queryKey: ['alarms', 'definition', door, id, 'summary'],
    queryFn: () => apiGet<AlarmSummary>(`/alarms/fleet/${door}/definition/${id}/summary`),
  })
}

/** Un rand din Alarms::activeAlarmsByAlert - o masina pe care suna definitia. */
export interface ActiveAlarm {
  ID: number | string
  Denumire: string | null
  value_numeric: number | string | null
  value_string: string | null
  hardware_identifier: string | null
  device_type: number | string | null
  Nume: string | null
  Prenume: string | null
  sign: string | null
  convert: string | null
  dinamic: number | string | null
}

export function activeAlarmsQuery(door: Door, id: number) {
  return queryOptions({
    queryKey: ['alarms', 'definition', door, id, 'active'],
    queryFn: () => apiGet<{ alarms: ActiveAlarm[] }>(`/alarms/fleet/${door}/definition/${id}/active`),
  })
}

/** Butonul Close de pe o alarma (closeAlarm din vechi). */
export function closeAlarm(door: Door, alarmId: number, description = ''): Promise<{ status: string; message?: string }> {
  return apiPost(`/alarms/fleet/${door}/alarm/${alarmId}/close`, { alarm: alarmId, description })
}
